import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@headlessui/react";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "./ResourceLoading";
import { ResourceError } from "./ResourceError";
import { ResourceEmpty } from "./ResourceEmpty";

export const PMCustomSelector = ({
  open: externalOpen,
  onClose: externalOnClose,
  onSelect,
  title = "Select Item",
  description = "Choose the item you want to work with",
  showSearch = true,
  multiple = false,
  selectedItems = [],
  filters = [],
  customRender,
  size = "lg",
  field = "name",
  field2 = "customer_number",
  field3 = "email",
  dataSource = null,
  fetchFunction = null,
  // Form integration props
  name,
  label,
  setFieldValue,
  values,
  errors,
  touched,
  selected,
  onChange,
  zIndex,
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(selectedItems);
  const [internalOpen, setInternalOpen] = useState(false);
  const scrollPositionRef = useRef(0);

  // Handle both external and internal open state
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const handleClose = externalOnClose || (() => setInternalOpen(false));

  useEffect(() => {
    // Load items when dataSource changes
    if (dataSource) {
      loadItems();
    }
  }, [dataSource]);
  // Preserve scroll position when opening/closing dialog
  useEffect(() => {
    if (isOpen) {
      // Store current scroll position when opening
      scrollPositionRef.current =
        window.pageYOffset || document.documentElement.scrollTop;
      // Prevent body scroll by fixing position
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = "100%";
    } else {
      // Restore scroll position when closing
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      // Restore scroll position
      window.scrollTo(0, scrollPositionRef.current);
    }

    // Cleanup function to restore scroll if component unmounts while open
    return () => {
      if (isOpen) {
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        window.scrollTo(0, scrollPositionRef.current);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && fetchFunction) {
      loadItems();
    }
  }, [isOpen]); // Removed loadItems from dependencies - it's stable

  useEffect(() => {
    if (isOpen && selectedItems) {
      setSelectedIds(selectedItems);
    }
  }, [isOpen]); // Removed selectedItems from dependencies to prevent infinite loop

  useEffect(() => {
    filterItems();
  }, [searchQuery, items]);

  const loadItems = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let itemList = [];

      // Use custom data source if provided
      if (dataSource) {
        // Handle both single objects and arrays
        if (Array.isArray(dataSource)) {
          itemList = dataSource;
        } else {
          // If it's a single object, wrap it in an array
          itemList = [dataSource];
        }
      }
      // Use custom fetch function if provided
      else if (fetchFunction) {
        const response = await fetchFunction();

        // Handle both array responses and nested object responses
        if (Array.isArray(response)) {
          itemList = response;
        } else if (response?.data) {
          if (Array.isArray(response.data)) {
            itemList = response.data;
          } else if (response.data.success && Array.isArray(response.data.data)) {
            itemList = response.data.data;
          } else if (Array.isArray(response.data.data)) {
            itemList = response.data.data;
          } else {
            itemList = [];
          }
        } else {
          itemList = [];
        }
      }
      // Require either dataSource or fetchFunction
      else {
        throw new Error("Either dataSource or fetchFunction must be provided");
      }

      // Apply filters if provided
      if (filters.length > 0) {
        itemList = itemList.filter((item) => {
          return filters.every((filter) => {
            if (filter.name === "status" && filter.value) {
              return item.status === filter.value;
            }
            if (filter.name === "category" && filter.value) {
              return item.category_id === filter.value;
            }
            if (filter.name === "active" && filter.value) {
              return item.active === filter.value;
            }
            if (filter.name === "type" && filter.value) {
              return item.customer_type === filter.value;
            }
            return true;
          });
        });
      }

      // Ensure all items have an id
      itemList = itemList.filter(item => item && item.id);
      
      setItems(itemList);
      setFilteredItems(itemList);
    } catch (err) {
      setError("Failed to load items");
      console.error("Error loading items:", err);
      setItems([]);
      setFilteredItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterItems = () => {
    if (!searchQuery.trim()) {
      setFilteredItems(items);
      return;
    }

    const filtered = items.filter(
      (item) =>
        (item[field] && String(item[field]).toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item[field2] && String(item[field2]).toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item[field3] && String(item[field3]).toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.phone && String(item.phone).toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.address && String(item.address).toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.title && String(item.title).toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.storage_number && String(item.storage_number).toLowerCase().includes(searchQuery.toLowerCase()))
    );
    setFilteredItems(filtered);
  };

  const handleSelectItem = (item) => {
    if (multiple) {
      const newSelectedIds = selectedIds.includes(item.id)
        ? selectedIds.filter((id) => id !== item.id)
        : [...selectedIds, item.id];
      setSelectedIds(newSelectedIds);
      
      // Update form values immediately for multiple selection
      if (setFieldValue && name) {
        setFieldValue(name, newSelectedIds);
        if (onChange) {
          const selectedItemsList = items.filter((i) => newSelectedIds.includes(i.id));
          onChange({
            target: {
              name: name,
              value: newSelectedIds,
              objects: selectedItemsList,
              data: selectedItemsList,
            },
          });
        }
      }
    } else {
      // Handle form integration
      if (setFieldValue && name) {
        setFieldValue(name, item.id);
        if (onChange) {
          onChange({
            target: {
              name: name,
              value: item.id,
              object: item,
              data: item,
            },
          });
        }
      } else if (onSelect) {
        onSelect(item);
      }
      // Close the dialog after selection
      setInternalOpen(false);
    }
  };

  const handleUnselect = () => {
    if (setFieldValue && name) {
      setFieldValue(name, null);
      if (onChange) {
        onChange({
          target: {
            name: name,
            value: null,
            object: null,
            data: null,
          },
        });
      }
    }
  };

  const handleConfirmSelection = () => {
    if (multiple) {
      const selectedItemsList = items.filter((item) =>
        selectedIds.includes(item.id)
      );
      if (setFieldValue && name) {
        setFieldValue(
          name,
          selectedItemsList.map((item) => item.id)
        );
        if (onChange) {
          onChange({
            target: {
              name: name,
              value: selectedItemsList.map((item) => item.id),
              objects: selectedItemsList,
              data: selectedItemsList,
            },
          });
        }
      } else if (onSelect) {
        onSelect(selectedItemsList);
      }
      handleClose();
    }
  };

  const isSelected = (item) => {
    if (multiple) {
      return selectedIds.includes(item.id);
    }
    // For single selection, check against form values or selected prop
    if (values && name) {
      return values[name] === item.id;
    }
    if (selected && selected.id) {
      return selected.id === item.id;
    }
    return false;
  };

  const getSelectedItem = () => {
    if (values && name && values[name]) {
      return items.find((item) => item.id === values[name]);
    }
    if (selected && selected.id) {
      return items.find((item) => item.id === selected.id);
    }
    return null;
  };

  const selectedItem = getSelectedItem();

  const renderSearchBar = () => {
    if (!showSearch) return null;

    return (
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("Search")}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder={t("Search items...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
    );
  };

  const renderItem = (item) => {
    if (customRender) {
      return customRender(item, isSelected(item), () => handleSelectItem(item));
    }

    return (
      <button
        key={item.id}
        onClick={() => handleSelectItem(item)}
        className={`w-full text-left p-4 border rounded-lg transition-colors ${
          isSelected(item)
            ? "border-blue-500 bg-blue-50"
            : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
        }`}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900">
              {item[field] || item.title || item.name || `Item ${item.id}`}
            </h4>
            {item[field2] && (
              <p className="text-sm text-gray-500 mt-1">
                {field2 === "customer_number" ? "Customer #: " : ""}
                {item[field2]}
              </p>
            )}
            {item[field3] && (
              <p className="text-sm text-gray-500 mt-1">
                {field3 === "email" ? "Email: " : ""}
                {item[field3]}
              </p>
            )}
            {item.phone && (
              <p className="text-sm text-gray-500 mt-1">Phone: {item.phone}</p>
            )}
            {item.address && (
              <p className="text-xs text-gray-400 mt-1">
                Address: {item.address}
              </p>
            )}
          </div>
          <div className="ml-4">
            {multiple && (
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  isSelected(item)
                    ? "border-blue-500 bg-blue-500"
                    : "border-gray-300"
                }`}
              >
                {isSelected(item) && (
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                )}
              </div>
            )}
            {!multiple && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {item.customer_type || "Item"}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="py-8">
          <ResourceLoading />
        </div>
      );
    }

    if (error) {
      return (
        <div className="py-8">
          <ResourceError errors={[error]} />
        </div>
      );
    }

    if (!filteredItems.length) {
      return (
        <div className="py-8">
          <ResourceEmpty
            text={
              searchQuery
                ? t("No items match your search")
                : items.length === 0
                ? t("No items available")
                : t("No items match your search")
            }
          />
          {items.length > 0 && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              Debug: {items.length} items loaded, but none match filter
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {renderSearchBar()}

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredItems.map((item) => renderItem(item))}
        </div>

        {multiple && selectedIds.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              {t("Selected")}: {selectedIds.length} {t("item(s)")}
            </p>
          </div>
        )}
      </div>
    );
  };

  const sizeClasses = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    "2xl": "sm:max-w-2xl",
  };

  // Render as a field component when used in forms
  if (name && label) {
    return (
      <>
        <div>
          <label
            htmlFor={name}
            className="block text-sm font-medium text-gray-700"
          >
            <label> {t(label)} </label>
          </label>
          <div className="relative mt-2 border-gray-300 focus-within:border-indigo-600">
            <div
              className="cursor-pointer block w-full border-0 border-b border-transparent bg-blue-50 focus:border-blue-500 focus:ring-0 sm:text-sm pr-10 p-2"
              onClick={() => setInternalOpen(true)}
            >
              {selectedItem ? (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-gray-900">
                      {selectedItem[field]}
                    </span>
                    {selectedItem[field2] && (
                      <p className="text-sm text-gray-500">
                        {selectedItem[field2]}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnselect();
                      }}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Clear
                    </button>
                    <span className="text-blue-600">Change</span>
                  </div>
                </div>
              ) : (
                <div className="text-gray-400">
                  Click to select {label.toLowerCase()}
                </div>
              )}
            </div>
          </div>

          {touched && touched[name] && errors && errors[name] && (
            <div className="flex mt-1 text-red-700 text-xs">
              <span className="ml-2">{errors[name]}</span>
            </div>
          )}
        </div>

        <Dialog
          as="div"
          className="fixed inset-0 overflow-y-auto"
          style={{ zIndex: zIndex ?? 3000 }}
          open={internalOpen}
          onClose={() => setInternalOpen(false)}
        >
          <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div
              className={`relative transform overflow-hidden rounded-lg bg-white px-6 pb-6 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full ${sizeClasses[size]} sm:p-6`}
            >
              <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  onClick={() => setInternalOpen(false)}
                >
                  <span className="sr-only">{t("Close")}</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t(title)}
                </h3>
                {description && (
                  <p className="text-sm text-gray-500">{t(description)}</p>
                )}
              </div>

              {renderContent()}

              <div className="mt-8 flex gap-3">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                  onClick={() => setInternalOpen(false)}
                >
                  {t("Cancel")}
                </button>

                {multiple && selectedIds.length > 0 && (
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    onClick={handleConfirmSelection}
                  >
                    {t("Confirm Selection")} ({selectedIds.length})
                  </button>
                )}
              </div>
            </div>
          </div>
        </Dialog>
      </>
    );
  }

  // Render as a standalone dialog when used directly
  return (
    <>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-y-auto"
        style={{ zIndex: zIndex ?? 3000 }}
        open={isOpen}
        onClose={handleClose}
      >
        <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
          <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div
            className={`relative transform overflow-hidden rounded-lg bg-white px-6 pb-6 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full ${sizeClasses[size]} sm:p-6`}
          >
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={handleClose}
              >
                <span className="sr-only">{t("Close")}</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t(title)}
              </h3>
              {description && (
                <p className="text-sm text-gray-500">{t(description)}</p>
              )}
            </div>

            {renderContent()}

            <div className="mt-8 flex gap-3">
              <button
                type="button"
                className="inline-flex w-full justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                onClick={handleClose}
              >
                {t("Cancel")}
              </button>

              {multiple && selectedIds.length > 0 && (
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  onClick={handleConfirmSelection}
                >
                  {t("Confirm Selection")} ({selectedIds.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};
